import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import { HandHeart, Heart, Mail, MessageCircle, Settings2, Check, X, Pause, Play, Square, Loader2, Save, Award } from 'lucide-react';

/**
 * Sección "Padrinos" del panel del albergue (reemplaza la lista antigua, que leía una tabla que no
 * existía y se caía apenas había un padrino). Datos en backend_php/sponsors.php.
 *  - Configuración: activar padrinos, monto mínimo mensual e instrucciones de aporte (se envían al aprobar).
 *  - Postulaciones que llegan desde Adóptame → aprobar / rechazar; luego pausar, reactivar o finalizar.
 *  - Contacto directo (correo / WhatsApp) y notas internas.
 * El dinero no pasa por Amigo Peludo: el padrino aporta directo al albergue según sus instrucciones.
 */

type Sponsor = {
    id: number; pet_id: number; pet_name: string; pet_image?: string | null;
    sponsor_name: string; sponsor_email: string; sponsor_phone?: string | null;
    monthly_amount: number; message?: string | null; status: string; shelter_notes?: string | null; created_at?: string;
};
type Settings = { enabled: boolean; min_amount: number; instructions: string };

const clp = (n: number) => `$${Number(n || 0).toLocaleString('es-CL')}`;
const STATUS: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Por aprobar', cls: 'bg-amber-100 text-amber-700' },
    active: { label: 'Activo', cls: 'bg-emerald-100 text-emerald-700' },
    paused: { label: 'Pausado', cls: 'bg-slate-100 text-slate-600' },
    ended: { label: 'Finalizado', cls: 'bg-slate-100 text-slate-400' },
    rejected: { label: 'Rechazado', cls: 'bg-rose-100 text-rose-600' },
};
const TABS: { id: string; label: string; match: string[] }[] = [
    { id: 'pending', label: 'Por aprobar', match: ['pending'] },
    { id: 'active', label: 'Activos', match: ['active'] },
    { id: 'paused', label: 'Pausados', match: ['paused'] },
    { id: 'closed', label: 'Finalizados', match: ['ended', 'rejected'] },
];

const DEMO: Sponsor[] = [
    { id: 1, pet_id: 101, pet_name: 'Pirata', pet_image: 'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=200', sponsor_name: 'Carlos Arriaga', sponsor_email: 'carlos@correo.cl', sponsor_phone: '+56912345678', monthly_amount: 15000, message: 'Quiero ayudar con su tratamiento.', status: 'active' },
    { id: 2, pet_id: 103, pet_name: 'Rambo', pet_image: 'https://images.unsplash.com/photo-1589941013453-ec89f33b5e95?w=200', sponsor_name: 'Sofía Valdés', sponsor_email: 'sofia@correo.cl', sponsor_phone: '', monthly_amount: 25000, message: '', status: 'active' },
    { id: 3, pet_id: 105, pet_name: 'Bigotes', pet_image: 'https://images.unsplash.com/photo-1573865526739-10659fec78a5?w=200', sponsor_name: 'Javiera Rojas', sponsor_email: 'javiera@correo.cl', sponsor_phone: '+56987654321', monthly_amount: 10000, message: 'Me enamoré de sus ojos 💛', status: 'pending' },
];

export const ShelterSponsors: React.FC<{ isDemo?: boolean }> = ({ isDemo }) => {
    const [sponsors, setSponsors] = useState<Sponsor[] | null>(null);
    const [settings, setSettings] = useState<Settings>({ enabled: false, min_amount: 5000, instructions: '' });
    const [draft, setDraft] = useState<Settings>(settings);
    const [showSettings, setShowSettings] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [tab, setTab] = useState('pending');
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState('');

    const load = async () => {
        if (isDemo) {
            setSponsors(DEMO);
            const s = { enabled: true, min_amount: 5000, instructions: 'Transferencia a Banco Estado, Cuenta Vista 12345678, RUT 76.123.456-7, a nombre de Albergue Huellitas. Indica en el comentario: "Padrino de <nombre>".' };
            setSettings(s); setDraft(s);
            return;
        }
        try {
            const res = await api.shelter.listSponsorships();
            if (res?.success) {
                setSponsors(res.sponsors || []);
                setSettings(res.settings); setDraft(res.settings);
                if (!res.settings.enabled) setShowSettings(true);
            } else { setSponsors([]); setError(res?.error || 'No se pudieron cargar los padrinos.'); }
        } catch { setSponsors([]); setError('Error de conexión.'); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isDemo]);

    // Si no hay pendientes, se abre la pestaña de activos.
    useEffect(() => {
        if (sponsors && !sponsors.some(s => s.status === 'pending') && tab === 'pending') setTab('active');
    }, [sponsors]); // eslint-disable-line react-hooks/exhaustive-deps

    const stats = useMemo(() => {
        const list = sponsors || [];
        const active = list.filter(s => s.status === 'active');
        return {
            active: active.length,
            monthly: active.reduce((t, s) => t + Number(s.monthly_amount || 0), 0),
            pending: list.filter(s => s.status === 'pending').length,
            pets: new Set(active.map(s => s.pet_id)).size,
        };
    }, [sponsors]);

    const visible = (sponsors || []).filter(s => (TABS.find(t => t.id === tab)?.match || []).includes(s.status));

    const setStatus = async (sp: Sponsor, status: string, notes?: string) => {
        if (status === 'rejected' && !confirm(`¿Rechazar la postulación de ${sp.sponsor_name}? Le avisaremos por correo.`)) return;
        if (status === 'ended' && !confirm(`¿Finalizar el padrinazgo de ${sp.sponsor_name} para ${sp.pet_name}?`)) return;
        setBusyId(sp.id); setError('');
        try {
            if (!isDemo) {
                const res = await api.shelter.updateSponsorship({ id: sp.id, status, ...(notes !== undefined ? { notes } : {}) });
                if (!res?.success) { setError(res?.error || 'No se pudo actualizar.'); return; }
            }
            setSponsors(prev => (prev || []).map(s => s.id === sp.id ? { ...s, status, ...(notes !== undefined ? { shelter_notes: notes } : {}) } : s));
        } finally { setBusyId(null); }
    };

    const saveSettings = async () => {
        if (draft.enabled && !draft.instructions.trim()) { setError('Escribe las instrucciones de aporte antes de activar los padrinos.'); return; }
        setSavingSettings(true); setError('');
        try {
            if (!isDemo) {
                const res = await api.shelter.saveSponsorSettings(draft);
                if (!res?.success) { setError(res?.error || 'No se pudo guardar.'); return; }
                setSettings(res.settings); setDraft(res.settings);
            } else setSettings(draft);
            setShowSettings(false);
        } finally { setSavingSettings(false); }
    };

    if (sponsors === null) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-orange-500" size={32} /></div>;

    return (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            {/* Encabezado y resumen */}
            <div className="bg-gradient-to-br from-rose-500 via-orange-500 to-amber-400 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden">
                <HandHeart size={140} className="absolute -right-6 -bottom-8 text-white/15" />
                <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/80">Padrinos</p>
                        <h2 className="text-3xl font-black uppercase italic tracking-tighter mt-1">Quienes no pueden adoptar, apadrinan</h2>
                        <p className="text-sm font-medium text-white/85 mt-2 max-w-xl">
                            {settings.enabled ? 'Tus rescatados muestran el botón “Apadrinar” en Adóptame. Las postulaciones llegan aquí.' : 'Los padrinos están desactivados: actívalos para que aparezca “Apadrinar” en Adóptame.'}
                        </p>
                    </div>
                    <button onClick={() => setShowSettings(s => !s)} className="shrink-0 bg-white text-orange-600 px-6 py-3.5 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-2 shadow-lg hover:scale-105 transition-transform">
                        <Settings2 size={16} /> {settings.enabled ? 'Configuración' : 'Activar padrinos'}
                    </button>
                </div>
                <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
                    {[
                        ['Padrinos activos', String(stats.active)],
                        ['Aporte mensual comprometido', clp(stats.monthly)],
                        ['Rescatados apadrinados', String(stats.pets)],
                        ['Por aprobar', String(stats.pending)],
                    ].map(([l, v]) => (
                        <div key={l} className="bg-white/15 backdrop-blur rounded-2xl px-4 py-3">
                            <p className="text-2xl font-black">{v}</p>
                            <p className="text-[9px] font-black uppercase tracking-widest text-white/80">{l}</p>
                        </div>
                    ))}
                </div>
            </div>

            {error && <p className="text-sm font-bold text-rose-600 bg-rose-50 rounded-2xl px-5 py-3">{error}</p>}

            {/* Configuración */}
            {showSettings && (
                <div className="bg-white rounded-[2.5rem] p-8 shadow-xl border border-slate-100 space-y-5 animate-in fade-in">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <h3 className="font-black text-slate-800 uppercase italic text-lg">Configuración de padrinos</h3>
                            <p className="text-xs font-bold text-slate-400">El aporte se hace directo a tu albergue: Amigo Peludo no cobra ni recibe el dinero.</p>
                        </div>
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{draft.enabled ? 'Activado' : 'Desactivado'}</span>
                            <input type="checkbox" className="sr-only peer" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} />
                            <span className="w-12 h-7 rounded-full bg-slate-200 peer-checked:bg-emerald-500 relative transition-colors after:content-[''] after:absolute after:top-1 after:left-1 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                        </label>
                    </div>
                    <div className="grid md:grid-cols-[14rem_1fr] gap-5">
                        <label className="block">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Aporte mínimo mensual (CLP)</span>
                            <input type="number" min={1000} step={1000} value={draft.min_amount} onChange={e => setDraft({ ...draft, min_amount: Number(e.target.value) })}
                                className="mt-2 w-full px-5 py-4 rounded-2xl bg-slate-50 border border-slate-100 font-black text-slate-700 outline-none focus:border-orange-400" />
                            <span className="text-[10px] font-bold text-slate-400 mt-1 block">Sugeriremos {clp(draft.min_amount)}, {clp(draft.min_amount * 2)} y {clp(draft.min_amount * 4)}.</span>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Instrucciones de aporte (se envían al aprobar un padrino)</span>
                            <textarea value={draft.instructions} onChange={e => setDraft({ ...draft, instructions: e.target.value })} rows={4} maxLength={2000}
                                placeholder="Ej: Transferencia a Banco Estado, Cuenta Vista 12345678, RUT 76.123.456-7, a nombre de Albergue Huellitas. Indica en el comentario: “Padrino de <nombre>”."
                                className="mt-2 w-full px-5 py-4 rounded-2xl bg-slate-50 border border-slate-100 text-sm font-medium text-slate-700 outline-none focus:border-orange-400 resize-none" />
                        </label>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button onClick={() => { setDraft(settings); setShowSettings(false); }} className="px-6 py-3.5 rounded-2xl bg-slate-100 text-slate-600 font-black uppercase text-[10px] tracking-widest">Cancelar</button>
                        <button onClick={saveSettings} disabled={savingSettings} className="px-6 py-3.5 rounded-2xl bg-orange-500 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-2 disabled:opacity-50">
                            {savingSettings ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
                        </button>
                    </div>
                </div>
            )}

            {/* Pestañas */}
            <div className="flex gap-2 overflow-x-auto pb-1">
                {TABS.map(t => {
                    const n = (sponsors || []).filter(s => t.match.includes(s.status)).length;
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`shrink-0 px-5 py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${tab === t.id ? 'bg-slate-900 text-white shadow-lg' : 'bg-white text-slate-500 border border-slate-100 hover:bg-slate-50'}`}>
                            {t.label} <span className={`ml-1.5 px-2 py-0.5 rounded-full ${tab === t.id ? 'bg-white/20' : 'bg-slate-100'}`}>{n}</span>
                        </button>
                    );
                })}
            </div>

            {/* Lista */}
            {visible.length === 0 ? (
                <div className="bg-white p-16 rounded-[3rem] text-center border border-slate-100 shadow-xl">
                    <Award className="mx-auto text-slate-200 mb-4" size={48} />
                    <p className="font-black uppercase italic text-slate-400 tracking-widest">
                        {tab === 'pending' ? 'No hay postulaciones por aprobar' : tab === 'active' ? 'Aún no tienes padrinos activos' : 'Nada por aquí'}
                    </p>
                    {!settings.enabled && <p className="text-xs font-bold text-slate-400 mt-2">Activa los padrinos para empezar a recibir postulaciones desde Adóptame.</p>}
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {visible.map(sp => {
                        const st = STATUS[sp.status] || STATUS.pending;
                        const wa = (sp.sponsor_phone || '').replace(/[^\d]/g, '');
                        return (
                            <div key={sp.id} className="bg-white rounded-[2.5rem] p-6 shadow-xl border border-slate-100 flex flex-col gap-5">
                                <div className="flex items-start gap-4">
                                    <div className="relative shrink-0">
                                        <img src={sp.pet_image || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(sp.pet_name)} alt="" className="w-16 h-16 rounded-2xl object-cover bg-slate-100" />
                                        <span className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full bg-rose-500 text-white flex items-center justify-center shadow"><Heart size={13} className="fill-white" /></span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-black text-slate-800 uppercase italic truncate">{sp.sponsor_name}</h4>
                                            <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                                        </div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Padrino de {sp.pet_name}</p>
                                        <p className="text-xs font-bold text-slate-500 truncate mt-1">{sp.sponsor_email}{sp.sponsor_phone ? ` · ${sp.sponsor_phone}` : ''}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-xl font-black text-emerald-600">{clp(sp.monthly_amount)}</p>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">al mes</p>
                                    </div>
                                </div>

                                {sp.message && <p className="text-sm text-slate-600 bg-slate-50 rounded-2xl px-4 py-3 italic">“{sp.message}”</p>}

                                <NotesField initial={sp.shelter_notes || ''} onSave={notes => setStatus(sp, sp.status, notes)} />

                                <div className="flex flex-wrap gap-2">
                                    {sp.status === 'pending' && (<>
                                        <ActionBtn busy={busyId === sp.id} onClick={() => setStatus(sp, 'active')} cls="bg-emerald-600 text-white"><Check size={14} /> Aprobar</ActionBtn>
                                        <ActionBtn busy={busyId === sp.id} onClick={() => setStatus(sp, 'rejected')} cls="bg-white text-rose-600 border border-rose-200"><X size={14} /> Rechazar</ActionBtn>
                                    </>)}
                                    {sp.status === 'active' && (<>
                                        <ActionBtn busy={busyId === sp.id} onClick={() => setStatus(sp, 'paused')} cls="bg-slate-100 text-slate-700"><Pause size={14} /> Pausar</ActionBtn>
                                        <ActionBtn busy={busyId === sp.id} onClick={() => setStatus(sp, 'ended')} cls="bg-white text-slate-500 border border-slate-200"><Square size={13} /> Finalizar</ActionBtn>
                                    </>)}
                                    {sp.status === 'paused' && (<>
                                        <ActionBtn busy={busyId === sp.id} onClick={() => setStatus(sp, 'active')} cls="bg-emerald-600 text-white"><Play size={14} /> Reactivar</ActionBtn>
                                        <ActionBtn busy={busyId === sp.id} onClick={() => setStatus(sp, 'ended')} cls="bg-white text-slate-500 border border-slate-200"><Square size={13} /> Finalizar</ActionBtn>
                                    </>)}
                                    <a href={`mailto:${sp.sponsor_email}?subject=${encodeURIComponent('Padrino de ' + sp.pet_name)}`} className="ml-auto px-4 py-2.5 rounded-xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5"><Mail size={14} /> Correo</a>
                                    {wa.length >= 8 && (
                                        <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`¡Hola ${sp.sponsor_name}! Te escribimos por tu padrinazgo de ${sp.pet_name} 💛`)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5"><MessageCircle size={14} /> WhatsApp</a>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ActionBtn: React.FC<{ onClick: () => void; busy?: boolean; cls: string; children: React.ReactNode }> = ({ onClick, busy, cls, children }) => (
    <button onClick={onClick} disabled={busy} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all disabled:opacity-50 ${cls}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : null}{children}
    </button>
);

/** Nota interna del albergue sobre el padrino (no la ve el padrino). */
const NotesField: React.FC<{ initial: string; onSave: (notes: string) => void }> = ({ initial, onSave }) => {
    const [v, setV] = useState(initial);
    const dirty = v !== initial;
    return (
        <div className="flex items-start gap-2">
            <textarea value={v} onChange={e => setV(e.target.value)} rows={1} maxLength={2000} placeholder="Nota interna (ej. aportó en octubre por transferencia)"
                className="flex-1 px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-100 text-xs font-medium text-slate-600 outline-none focus:border-orange-300 resize-none" />
            {dirty && <button onClick={() => onSave(v)} className="px-3 py-2.5 rounded-xl bg-orange-500 text-white text-[10px] font-black uppercase"><Save size={14} /></button>}
        </div>
    );
};

export default ShelterSponsors;
